import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// Cross-Club Upsell Leads — Sprint 21.4. Members on a home-club-restricted
// Unlimited plan (Local Unlimited, or "Unlimited | <club>") already paying
// full non-member rates at a different club — a real, validated Sprint 19.3
// finding, DFW-internal, always an upsell opportunity (Nationwide Unlimited)
// never a fraud problem. Outcomes write to the shared outcome_log
// (module='revenue_upsell'), so they show up on the member's timeline per
// the standing contact-visibility rule — no separate status table here.

const OUTCOMES = ['Contacted', 'Interested', 'Upgraded', 'Not Interested', 'No Answer'];

function UpsellCard({ lead, onLogOutcome, updating }) {
  const [formOpen, setFormOpen] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div style={{
      background: 'var(--surface-2, #111)', border: '1px solid var(--border, #222)',
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)' }}>{lead.name}</span>
            <span style={{
              background: '#ffffff11', color: '#aaa', border: '1px solid #333',
              borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
            }}>
              {lead.membershipType}
            </span>
            {lead.lastOutcome && (
              <span style={{
                background: '#2563eb22', color: '#60a5fa', border: '1px solid #2563eb',
                borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
              }}>
                Last: {lead.lastOutcome}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>
            {lead.email} · home club {lead.homeLocationId}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {lead.crossClubCharges} cross-club visits · ${lead.totalPaidAsNonMember.toFixed(2)} paid as non-member ·
            last visit {new Date(lead.lastCrossClubVisit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>
        <button
          onClick={() => setFormOpen(o => !o)}
          style={{
            background: formOpen ? 'var(--yellow)' : 'var(--surface-3, #1a1a1a)',
            color: formOpen ? '#000' : 'var(--text-muted)',
            border: '1px solid var(--border, #333)', borderRadius: 6,
            padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          }}
        >{formOpen ? 'Close' : 'Log Contact'}</button>
      </div>
      {formOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select
            className="field-input" value={outcome} onChange={e => setOutcome(e.target.value)}
            style={{ fontSize: 12, minWidth: 140 }}
          >
            <option value="">Outcome…</option>
            {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <input
            className="field-input" type="text" placeholder="Note (optional)"
            value={notes} onChange={e => setNotes(e.target.value)}
            style={{ flex: 1, minWidth: 160, fontSize: 12 }}
          />
          <button
            disabled={!outcome || updating}
            onClick={() => { onLogOutcome(lead.memberId, lead.homeLocationId, outcome, notes); setFormOpen(false); setOutcome(''); setNotes(''); }}
            style={{
              background: 'var(--yellow)', color: '#000', border: 'none',
              borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700,
              cursor: outcome ? 'pointer' : 'not-allowed', opacity: outcome ? 1 : 0.5,
            }}
          >Save</button>
        </div>
      )}
    </div>
  );
}

export default function CrossClubUpsell() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const clubId = currentClub?.location_id || currentClub?.id;
    get(`/revenue/cross-club-upsell?club=${clubId}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- get from useApi() is a new reference every render; including it causes an infinite fetch loop (confirmed live, 9/30/26)

  useEffect(() => { load(); }, [load]);

  async function handleLogOutcome(memberId, locationId, outcome, notes) {
    setUpdating(prev => ({ ...prev, [memberId]: true }));
    try {
      await post(`/revenue/cross-club-upsell/${memberId}/outcome`, { locationId, outcome, notes });
      setData(prev => prev ? ({
        ...prev,
        leads: prev.leads.map(l => l.memberId === memberId
          ? { ...l, lastOutcome: outcome, lastContactedAt: new Date().toISOString() }
          : l),
      }) : prev);
    } catch (e) {
      console.error('Cross-club upsell outcome log failed', e);
    } finally {
      setUpdating(prev => ({ ...prev, [memberId]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load Upsell Leads — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const leads = data?.leads || [];

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
        📈 <strong style={{ color: 'var(--text)' }}>{leads.length}</strong> members on a home-club-restricted Unlimited plan
        already paying full non-member rates elsewhere
      </div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 14 }}>
        These are upsell leads, not leakage — they're already effectively paying for cross-club access piecemeal.
        Pitch: Nationwide Unlimited covers every club they're already visiting.
      </div>

      {leads.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          No cross-club upsell leads in the current window.
        </div>
      ) : (
        leads.map(lead => (
          <UpsellCard key={lead.memberId} lead={lead} onLogOutcome={handleLogOutcome} updating={updating[lead.memberId]} />
        ))
      )}
    </div>
  );
}
